import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
  LinearProgress,
  Alert,
} from "@mui/material";
import { Headphones, X, Trash2, Sparkles } from "lucide-react";
import { resolveAudioUrl } from "./StoryAutoplayEngine";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";
import { transcribeMediaUrl } from "../VideoBlock/WhisperTranscriberEngine";

export const SectionAudioSubMenu = ({
  audioTrack = {},
  onUpdate,
  onMediaSelect,
  onClose,
  transcriber = transcribeMediaUrl,
}) => {
  const t = useScrollytellTranslation();
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcribeProgress, setTranscribeProgress] = useState(null);
  const [transcribeError, setTranscribeError] = useState(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleChange = (field, value) => {
    onUpdate({
      ...audioTrack,
      [field]: value,
    });
  };

  const handleAutoTranscribe = async () => {
    if (!audioTrack?.url || isTranscribing) return;

    setTranscribeError(null);
    setIsTranscribing(true);
    setTranscribeProgress({
      status: t(
        "sectionAudioSubMenu.transcribingStart",
        "Forbereder transkripsjon..."
      ),
      progress: 0,
    });

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const text = await transcriber(audioTrack.url, {
        signal: controller.signal,
        onProgress: (p) => {
          setTranscribeProgress({
            status: p.status || "",
            progress: typeof p.progress === "number" ? p.progress : 0,
          });
        },
      });

      if (text) {
        if (audioTrack.transcript && audioTrack.transcript.trim().length > 0) {
          const replace =
            typeof window !== "undefined" && window.confirm
              ? window.confirm(
                  t(
                    "sectionAudioSubMenu.replaceConfirm",
                    "Feltet inneholder allerede tekst. Trykk OK for å erstatte, eller Avbryt for å tilføye på slutten."
                  )
                )
              : true;
          if (replace) {
            handleChange("transcript", text);
          } else {
            handleChange(
              "transcript",
              `${audioTrack.transcript.trim()}\n\n${text}`
            );
          }
        } else {
          handleChange("transcript", text);
        }
      }
    } catch (err) {
      if (err.name === "AbortError" || err.message?.includes("avbrutt")) {
        // Cancelled by user
      } else {
        setTranscribeError(
          err.message ||
            t(
              "sectionAudioSubMenu.transcribeError",
              "Kunne ikke hente lyd fra lydsporet. Kontroller at lydfilen er tilgjengelig."
            )
        );
      }
    } finally {
      setIsTranscribing(false);
      setTranscribeProgress(null);
      abortControllerRef.current = null;
    }
  };

  const handleCancelTranscribe = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsTranscribing(false);
    setTranscribeProgress(null);
  };

  const handleSelectMedia = () => {
    if (onMediaSelect) {
      onMediaSelect("audio", (selectedUrlOrId) => {
        const resolved = resolveAudioUrl(selectedUrlOrId);
        if (resolved) {
          handleChange("url", resolved);
        }
      });
    }
  };

  const containerRef = useRef(null);
  const [maxMenuHeight, setMaxMenuHeight] = useState("calc(100vh - 280px)");

  const setContainerRef = useCallback((node) => {
    containerRef.current = node;
    if (node && typeof window !== "undefined") {
      const rect = node.getBoundingClientRect();
      if (rect.top > 0 && window.innerHeight > 0) {
        const available = Math.max(200, Math.floor(window.innerHeight - rect.top - 16));
        setMaxMenuHeight(`${available}px`);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateHeight = () => {
      const node = containerRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      if (rect.top > 0 && window.innerHeight > 0) {
        const available = Math.max(200, Math.floor(window.innerHeight - rect.top - 16));
        setMaxMenuHeight(`${available}px`);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    window.addEventListener("scroll", updateHeight, true);

    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("scroll", updateHeight, true);
    };
  }, []);

  return (
    <Box
      ref={setContainerRef}
      data-testid="section-audio-submenu-container"
      style={{
        maxHeight: maxMenuHeight,
        overflowY: "auto",
        overscrollBehavior: "contain",
        boxSizing: "border-box",
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: 2,
        backgroundColor: "#FFFFFF",
        color: "#222222",
        borderRadius: "4px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
        border: "1px solid #E0E0E0",
        minWidth: 320,
        maxHeight: maxMenuHeight,
        overflowY: "auto",
        overscrollBehavior: "contain",
        boxSizing: "border-box",
        "& > *": {
          flexShrink: 0,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Headphones size={20} color="#0284C7" />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#111827" }}>
            {t("sectionAudioSubMenu.title", "Lydspor for kapittel")}
          </Typography>
        </Box>
        {onClose && (
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("sectionAudioSubMenu.close", "Lukk lydmeny")}
            sx={{ color: "#64748B", "&:hover": { color: "#0F172A" } }}
          >
            <X size={18} />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          border: "1px solid #CBD5E1",
          borderRadius: "6px",
          p: 1.5,
          backgroundColor: "#F8FAFC",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600 }}>
            {t("sectionAudioSubMenu.audioFileLabel", "Lydfil")}
          </Typography>
          {Boolean(audioTrack.url) && (
            <IconButton
              size="small"
              onClick={() => handleChange("url", "")}
              aria-label={t("sectionAudioSubMenu.removeAudio", "Fjern lyd")}
              title={t("sectionAudioSubMenu.removeAudio", "Fjern lyd")}
              sx={{ color: "#EF4444", p: 0.5, "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.1)" } }}
            >
              <Trash2 size={16} />
            </IconButton>
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: audioTrack.url ? "#0F172A" : "#64748B",
            fontWeight: audioTrack.url ? 600 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {audioTrack.url
            ? audioTrack.url.split("/").pop() || audioTrack.url
            : t("sectionAudioSubMenu.noAudioSelected", "Ingen lydfil valgt")}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={handleSelectMedia}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#0284C7",
            color: "#0284C7",
            "&:hover": { borderColor: "#0369A1", backgroundColor: "rgba(2, 132, 199, 0.08)" },
          }}
        >
          {t("sectionAudioSubMenu.chooseAudio", "Velg lyd fra mediebibliotek")}
        </Button>
      </Box>

      <TextField
        fullWidth
        size="small"
        label={t("sectionAudioSubMenu.trackTitleLabel", "Tittel på lydspor")}
        value={audioTrack.title || ""}
        onChange={(e) => handleChange("title", e.target.value)}
        placeholder="F.eks. Fortellerstemme: Introduksjon"
      />

      {Boolean(audioTrack?.url) && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<Sparkles size={16} />}
              onClick={handleAutoTranscribe}
              disabled={isTranscribing}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderColor: "#0284C7",
                color: "#0284C7",
                "&:hover": {
                  borderColor: "#0369A1",
                  backgroundColor: "rgba(2, 132, 199, 0.08)",
                },
              }}
            >
              {t(
                "sectionAudioSubMenu.autoTranscribeBtn",
                "Generer transkripsjon automatisk (AI)"
              )}
            </Button>

            {isTranscribing && (
              <Button
                variant="text"
                size="small"
                onClick={handleCancelTranscribe}
                sx={{
                  textTransform: "none",
                  fontSize: "12px",
                  color: "#666666",
                }}
              >
                {t("sectionAudioSubMenu.cancelTranscribe", "Avbryt")}
              </Button>
            )}
          </Box>

          {isTranscribing && (
            <Box
              role="status"
              aria-live="polite"
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: "#F0F9FF",
                border: "1px solid #BAE6FD",
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#0369A1", fontWeight: 600 }}
              >
                {transcribeProgress?.status ||
                  t(
                    "sectionAudioSubMenu.transcribingWait",
                    "Transkriberer tale med NB-Whisper AI..."
                  )}
              </Typography>
              <LinearProgress
                variant={
                  transcribeProgress?.progress > 0
                    ? "determinate"
                    : "indeterminate"
                }
                value={transcribeProgress?.progress || 0}
                aria-valuenow={transcribeProgress?.progress || 0}
                aria-valuemin={0}
                aria-valuemax={100}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#E0F2FE",
                  "& .MuiLinearProgress-bar": { backgroundColor: "#0284C7" },
                }}
              />
            </Box>
          )}

          {transcribeError && (
            <Alert severity="error" onClose={() => setTranscribeError(null)}>
              {transcribeError}
            </Alert>
          )}
        </Box>
      )}

      <TextField
        fullWidth
        multiline
        rows={4}
        size="small"
        label={t("sectionAudioSubMenu.transcriptLabel", "Transkripsjon (tekstversjon)")}
        value={audioTrack.transcript || ""}
        onChange={(e) => handleChange("transcript", e.target.value)}
        placeholder="Fullstendig tekst som leses opp i lydsporet for universell utforming (WCAG 1.2.2)"
        helperText={t("sectionAudioSubMenu.transcriptHelperText", "Påkrevd for å oppfylle WCAG 1.2.2")}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(audioTrack.autoAdvanceOnEnd)}
            onChange={(e) => handleChange("autoAdvanceOnEnd", e.target.checked)}
          />
        }
        label={t(
          "sectionAudioSubMenu.autoAdvance",
          "Gå automatisk til neste kapittel når lydsporet er ferdig"
        )}
        sx={{
          color: "#222222",
          "& .MuiFormControlLabel-label": {
            color: "#222222",
            fontSize: "13px",
          },
        }}
      />
    </Box>
  );
};
